import React from "react";
import { useTranslation } from "react-i18next";

import ErrorToast from "@components/Common/ErrorTost";

import { useFetchGlobal } from "../../../../api/hooks";
import { PrinterAppId, PrinterRowData } from "../types";
import { setSalesModes } from "../stores/orchestrator";
import { buildSalesModes, SalesModeApiItem } from "./salesModesHelper";

const HTTP_GET = "GET";
const CONTENT_TYPE_JSON = "application/json";

type ModeOfSaleResponse = {
    data?: SalesModeApiItem[];
};

export type UseModeOfSaleParams = {
    enabled: boolean;
    endpoint: string;
    appId: PrinterAppId;
    rowData: PrinterRowData;
};

export type UseModeOfSaleResult = {
    rawSalesModes: SalesModeApiItem[];
    reset: () => void;
};

export function useModeOfSale({
    enabled,
    endpoint,
    appId,
    rowData,
}: UseModeOfSaleParams): UseModeOfSaleResult {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const [rawSalesModes, setRawSalesModes] = React.useState<
        SalesModeApiItem[]
    >([]);

    // fetchGlobal et t sont recréés à chaque render (non mémoïsés en amont),
    // et rowData est un objet dont l'identité change : on les garde dans des
    // refs pour ne PAS relancer le fetch à chaque render. Le fetch ne dépend
    // que des primitives stables endpoint/appId.
    const fetchGlobalRef = React.useRef(fetchGlobal);
    fetchGlobalRef.current = fetchGlobal;
    const rowDataRef = React.useRef(rowData);
    rowDataRef.current = rowData;
    const tRef = React.useRef(t);
    tRef.current = t;

    const fetchModeOfSale = React.useCallback(async () => {
        try {
            const response = await fetchGlobalRef.current<ModeOfSaleResponse>(
                endpoint,
                {
                    method: HTTP_GET,
                    headers: { "Content-Type": CONTENT_TYPE_JSON },
                }
            );
            const data = response.data ?? [];
            setRawSalesModes(data);
            setSalesModes(buildSalesModes(data, rowDataRef.current, appId));
        } catch {
            ErrorToast(`${tRef.current("There's an error")!}`);
        }
    }, [endpoint, appId]);

    React.useEffect(() => {
        if (!enabled) return;
        fetchModeOfSale();
    }, [enabled, fetchModeOfSale]);

    const reset = React.useCallback(() => {
        setRawSalesModes([]);
        setSalesModes({});
    }, []);

    return { rawSalesModes, reset };
}
